<div *ngIf="errorMessage" class="bg-red-50 text-red-700 border border-red-200 rounded-md px-4 py-3 mb-4 text-sm">
  <i class="fas fa-exclamation-circle mr-1"></i> {{ errorMessage }}
</div>

<div *ngIf="loading" class="text-center text-gray-500 py-10">
  <i class="fas fa-spinner fa-spin mr-1"></i> Loading delivery health...
</div>

<div *ngIf="!loading && report && banner">
  <!-- Health headline -->
  <div class="border rounded-lg px-4 py-3 mb-4 flex items-start gap-3" [ngClass]="banner.classes">
    <i [class]="banner.icon + ' mt-0.5'"></i>
    <div>
      <div class="font-semibold">{{ banner.title }}</div>
      <div class="text-sm">{{ banner.detail }}</div>
    </div>
  </div>

  <!-- Outcome tiles -->
  <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Accepted</div>
      <div class="text-2xl font-bold text-green-600">{{ report.totals.sent }}</div>
    </div>
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Rejected</div>
      <div class="text-2xl font-bold text-red-600">{{ report.totals.rejected }}</div>
    </div>
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Failed</div>
      <div class="text-2xl font-bold text-red-600">{{ report.totals.error }}</div>
      <div class="text-xs text-gray-400">network, after retries</div>
    </div>
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Skipped</div>
      <div class="text-2xl font-bold text-gray-700">{{ report.totals.skipped }}</div>
      <div class="text-xs text-gray-400">kill switch off</div>
    </div>
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Success rate</div>
      <div class="text-2xl font-bold text-gray-900">{{ report.totals.successRate != null ? (report.totals.successRate + '%') : '—' }}</div>
      <div class="text-xs text-gray-400">of events that reached Meta</div>
    </div>
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Last accepted</div>
      <div class="text-sm font-bold text-gray-900 mt-2">{{ report.lastSentAt ? (report.lastSentAt | date:'d MMM, h:mm a') : 'Never in this period' }}</div>
    </div>
  </div>

  <!-- Daily trend -->
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6" *ngIf="report.daily.length > 0">
    <div class="bg-white rounded-lg border border-gray-200 p-3">
      <app-chart-wrapper [data]="sentChart" title="Accepted by Meta, per day" type="bar" [colors]="['#16a34a']" [height]="240"></app-chart-wrapper>
    </div>
    <div class="bg-white rounded-lg border border-gray-200 p-3">
      <app-chart-wrapper [data]="failedChart" title="Rejected or failed, per day" type="bar" [colors]="['#dc2626']" [height]="240"></app-chart-wrapper>
    </div>
  </div>

  <!-- By route -->
  <div class="bg-white rounded-lg border border-gray-200 mb-6 overflow-x-auto" *ngIf="report.byRoute.length > 0">
    <div class="px-4 py-3 border-b border-gray-200 text-sm font-bold text-gray-700 uppercase">By route</div>
    <table class="min-w-full text-sm">
      <thead class="bg-gray-50 text-xs text-gray-500 uppercase">
        <tr>
          <th class="text-left px-4 py-2">Route</th>
          <th class="text-right px-4 py-2">Accepted</th>
          <th class="text-right px-4 py-2">Rejected</th>
          <th class="text-right px-4 py-2">Failed</th>
          <th class="text-right px-4 py-2">Skipped</th>
          <th class="text-right px-4 py-2">Success rate</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let route of report.byRoute" class="border-t border-gray-100">
          <td class="px-4 py-2 text-gray-900">{{ routeLabel(route.actionSource) }}</td>
          <td class="px-4 py-2 text-right text-green-700">{{ route.sent }}</td>
          <td class="px-4 py-2 text-right text-red-700">{{ route.rejected }}</td>
          <td class="px-4 py-2 text-right text-red-700">{{ route.error }}</td>
          <td class="px-4 py-2 text-right text-gray-600">{{ route.skipped }}</td>
          <td class="px-4 py-2 text-right font-semibold">{{ route.successRate != null ? (route.successRate + '%') : '—' }}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Top errors -->
  <div class="bg-white rounded-lg border border-gray-200 mb-4 overflow-x-auto" *ngIf="report.topErrors.length > 0">
    <div class="px-4 py-3 border-b border-gray-200 text-sm font-bold text-gray-700 uppercase">Top errors</div>
    <table class="min-w-full text-sm">
      <thead class="bg-gray-50 text-xs text-gray-500 uppercase">
        <tr>
          <th class="text-right px-4 py-2">Count</th>
          <th class="text-left px-4 py-2">Error</th>
          <th class="text-left px-4 py-2">Route</th>
          <th class="text-left px-4 py-2">What it means</th>
          <th class="text-left px-4 py-2">Last seen</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let error of report.topErrors" class="border-t border-gray-100 align-top">
          <td class="px-4 py-2 text-right font-semibold">{{ error.count }}</td>
          <td class="px-4 py-2 whitespace-nowrap">
            <span class="font-mono text-xs">{{ error.status }}<ng-container *ngIf="error.errorCode != null"> {{ error.errorCode }}</ng-container><ng-container *ngIf="error.errorSubcode != null"> / {{ error.errorSubcode }}</ng-container></span>
          </td>
          <td class="px-4 py-2 text-gray-700">{{ routeLabel(error.actionSource) }}</td>
          <td class="px-4 py-2 text-gray-700">
            <div *ngIf="errorHint(error) as hint" class="font-medium text-gray-900">{{ hint }}</div>
            <div *ngIf="error.sampleMessage" class="text-xs text-gray-500">Meta: {{ error.sampleMessage }}</div>
          </td>
          <td class="px-4 py-2 text-gray-600 whitespace-nowrap">{{ error.lastSeenAt | date:'d MMM, h:mm a' }}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <p class="text-xs text-gray-400">
    Each conversion counts once, by its latest send attempt. Conversions logged before send outcomes were recorded are not included.
    A web sign-up's CompleteRegistration event shares its Lead's row, so only the Lead is counted here.
  </p>
</div>
